import styled from "styled-components";
import {useCallback, useEffect, useRef, useState} from "react";
import {PopupV5} from "../../ui/5.0/popup/PopupV5";
import {SelectInputStyled, SelectInputV5} from "../../ui/5.0/input/select/SelectInputV5";
import {ClickToEditInputStyled, ClickToEditInputV5} from "../../ui/5.0/input/clicktoedit/ClickToEditInputV5";
import {FieldV5} from "../../ui/5.0/details/FieldV5";
import {Functions} from "../function/Functions";
import {FieldStyled} from "../../ui/5.0/details/Field.styled";
import {ButtonStyled, ButtonV5} from "../../ui/5.0/button/ButtonV5";
import CameraIcon from "../../ui/5.0/icons/camera-new.svg?react";
import {colorPalette} from "../../ui/5.0/styles/colorPalette";
import {fontSize, fontWeight, InfoIconStyled, zIndex} from "../../ui/5.0/styles/styles";
import {PopupBackdropV5} from "../../ui/5.0/popup/PopupBackdropV5";
import {DomPortal} from "../../ui/modules/abstract/portal/DomPortal";
import {useAppStore} from "../../StateManager";

interface IWebCamProps {
	readonly onClose: () => void;
	readonly onComplete: (data: {base64: string; fileName: string; extension: string}) => void;
}

interface ICameraOption {
	label: string;
	deviceId: string;
}

export const WebCamV5 = (props: IWebCamProps) => {
	const appState = useAppStore((state) => state.appState);
	const [cameraOptions, setCameraOptions] = useState<ICameraOption[]>([]);
	const [selectedCamera, setSelectedCamera] = useState<ICameraOption | null>(null);

	const timerMax = 60;
	const [timerValue, setTimerValue] = useState<number>(0);
	const [timer, setTimer] = useState<number>(0);

	const [capturedImage, setCapturedImage] = useState<string | null>(null);
	const [capturedImageName, setCapturedImageName] = useState<string | null>(null);
	const [capturedImageExtension, setCapturedImageExtension] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);

	const videoRef = useRef<HTMLVideoElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const streamRef = useRef<MediaStream | null>(null);

	const getCameraDevices = async () => {
		const devices = await navigator.mediaDevices.enumerateDevices();

		return devices
			.filter((device) => device.kind === "videoinput")
			.map((device) => ({
				label: device.label || "Camera",
				deviceId: device.deviceId,
			}));
	};

	const handleTimerInputChange = (newValue: string) => {
		const valueAsNumber = parseInt(newValue, 10);
		const safeValue = isNaN(valueAsNumber) ? 0 : valueAsNumber;

		setTimerValue(safeValue);
	};

	const handleCapturedImageNameChange = (newValue: string) => {
		setCapturedImageName(newValue);
	};

	const getErrorMessage = (value: string) => {
		const timerValue = parseInt(value, 10);
		if (timerValue < 0) {
			return "Time should be greater than 0";
		} else if (timerValue > timerMax) {
			return "Maximum time is 60s";
		}
	};

	const getPhotoNameError = (value: string) => {
		return value.trim() === "" ? "Photo name cannot be empty" : "";
	};

	const onCancelClick = () => {
		if (timer > 0 || capturedImage) {
			handleRetake();
		} else {
			if (!capturedImage) {
				props.onClose();
			}
		}
	};

	const handleCapture = () => {
		if (timerValue > 1) {
			//If user clicked on Capture after entering the timer value
			setTimer(timerValue);
		} else {
			capturePhoto();
		}
	};

	const capturePhoto = () => {
		if (videoRef.current && canvasRef.current) {
			const video = videoRef.current;
			const canvas = canvasRef.current;
			canvas.width = video.videoWidth;
			canvas.height = video.videoHeight;
			const ctx = canvas.getContext("2d");

			if (ctx) {
				ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

				const extension: "webp" | "jpg" = "webp";
				const format: "webp" | "jpeg" = (extension as string) === "jpg" ? "jpeg" : "webp";

				const fileName = new Date().toLocaleString().replaceAll(", ", "_").replaceAll(" ", "_");
				const image = canvas.toDataURL(`image/${format}`, 0.78);

				setCapturedImage(image);
				setCapturedImageName(fileName);
				setCapturedImageExtension(format);
			}
		}
	};

	const handleRetake = async () => {
		setCapturedImage(null);
		setCapturedImageName(null);
		setCapturedImageExtension(null);
		setTimerValue(0);
		setTimer(0);

		// Restart the stream (this ensures video element gets a valid stream again)
		await startStream();
	};

	const handleConfirmUpload = (capturedImageSrc: string, fileName: string, extension: string) => {
		if (!capturedImageSrc) {
			return;
		}

		props.onComplete({
			base64: capturedImageSrc,
			fileName,
			extension,
		});
	};

	const startStream = useCallback(async () => {
		if (!selectedCamera) {
			return;
		}

		try {
			// Stop previous stream
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
			}

			const stream = await navigator.mediaDevices.getUserMedia({
				video: {deviceId: selectedCamera.deviceId},
				audio: false,
			});

			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				await videoRef.current.play();

				//const { videoWidth, videoHeight } = videoRef.current;
				//const aspectRatio = videoWidth / videoHeight;
				//const visualWidth = 1024;
				//const visualHeight = visualWidth / aspectRatio;

				//New UI design
				const visualWidth = 1024;
				const visualHeight = 576;

				videoRef.current.style.width = `${visualWidth}px`;
				videoRef.current.style.height = `${visualHeight}px`;
			}

			streamRef.current = stream;
		} catch (err) {
			console.error("Error accessing camera:", err);
		}
	}, [selectedCamera]);

	useEffect(() => {
		const fetchCameras = async () => {
			try {
				setLoading(true);

				const tempStream = await navigator.mediaDevices.getUserMedia({video: true, audio: false});

				const devices = await getCameraDevices();
				setCameraOptions(devices);

				if (devices.length > 0) {
					setSelectedCamera(devices[0]); // default select first
				}

				tempStream.getTracks().forEach((track) => track.stop());
			} catch (err) {
				console.error("Camera access denied or error occurred:", err);
			} finally {
				setLoading(false); // stop loading
			}
		};

		fetchCameras();
	}, []);

	useEffect(() => {
		if (timer === 0) {
			return;
		} // If the timer is 0, don't start the interval.

		const interval = setInterval(() => {
			setTimer((prev) => {
				if (prev > 1) {
					return prev - 1;
				} else {
					clearInterval(interval);
					capturePhoto();
					return 0;
				}
			});
		}, 1000);

		// Cleanup the interval when the component is unmounted or the timer changes
		return () => clearInterval(interval);
	}, [timer]);

	useEffect(() => {
		startStream();

		return () => {
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
			}
		};
	}, [startStream]);

	const isRetake = Boolean(capturedImage);
	const buttonLabel = isRetake ? "Retake" : "Capture";

	return (
		<DomPortal destination={appState.app.modalContainer}>
			<PopupBackdropV5 onClick={Functions.stopPropagation}>
				<PopupV5
					onClose={props.onClose}
					className="CapturePhoto"
					label={"Take a Photo"}
					centerOnScreen={true}
					freezeRoot={true}
					noButtons={true}
					width="1056px"
					height="744px"
					isSmallPopup={false}
					closeOnClickOutside={false}
				>
					<WebCamStyled>
						<WebCamHeaderStyled>
							<div className="timerHeader">
								{!capturedImage ? (
									<>
										<SelectInputV5
											selected={selectedCamera}
											onChange={setSelectedCamera}
											options={cameraOptions}
											isSameWidth={true}
											renderOptionWhenClosed={(device: ICameraOption) => device.label}
											optionsZIndex={zIndex.webcamPopup + 1}
										/>
										<FieldV5
											className="TypeSelector"
											label="Timer (seconds)"
										>
											<ClickToEditInputV5
												value={timerValue}
												placeholder={"0"}
												inputType="number"
												onChange={handleTimerInputChange}
												getErrorMessage={getErrorMessage}
												onBlur={Functions.emptyFunction}
												disabled={timer > 0}
											/>
										</FieldV5>
									</>
								) : (
									<FieldV5
										className="PhotoName"
										label="Photo Name "
									>
										<ClickToEditInputV5
											value={capturedImageName}
											className="capturedImage"
											onChange={handleCapturedImageNameChange}
											getErrorMessage={getPhotoNameError}
										/>
									</FieldV5>
								)}
							</div>

							<ButtonV5
								className={buttonLabel}
								title={buttonLabel}
								label={buttonLabel}
								type={isRetake ? "secondary" : null}
								IconComponent={CameraIcon}
								onClick={isRetake ? handleRetake : handleCapture}
								disabled={timer > 0}
							/>
						</WebCamHeaderStyled>

						<VideoContainer>
							{capturedImage ? (
								<img
									src={capturedImage}
									alt="Captured"
									style={{borderRadius: "8px"}}
								/>
							) : (
								<div className="VideoContainer">
									{timer > 0 && <div className="TimerOverlay">{timer}</div>}
									{loading && (
										<div className="LoadingOverlay">
											<p>Loading...</p>
										</div>
									)}
									<video
										ref={videoRef}
										autoPlay
										playsInline
										muted
									/>
								</div>
							)}
							<canvas
								ref={canvasRef}
								style={{display: "none", width: "1024px", height: "576px"}}
							/>
						</VideoContainer>

						<div className="buttonContainer">
							<ButtonV5
								className="tertiary"
								type="tertiary"
								title="Cancel"
								label="Cancel"
								onClick={onCancelClick}
							/>
							<ButtonV5
								className="secondary"
								title="Confirm"
								label="Confirm"
								disabled={!capturedImage}
								onClick={() => handleConfirmUpload(capturedImage, capturedImageName, capturedImageExtension)}
							/>
						</div>
					</WebCamStyled>
				</PopupV5>
			</PopupBackdropV5>
		</DomPortal>
	);
};

const VideoContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 8px;

	video,
	img {
		width: 1024px;
		height: 576px;
		background-color: black;
		object-fit: cover;
		border-radius: 8px;
	}

	.VideoContainer {
		position: relative;
		width: fit-content;
	}

	// Shared overlay base styles
	.TimerOverlay,
	.LoadingOverlay {
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		color: ${colorPalette.white};
		opacity: 50%;
		padding: 4px 8px;
		border-radius: 6px;
		z-index: 10;
		transition: all 0.3s ease;
	}

	.TimerOverlay {
		top: 35%;
		font-weight: ${fontWeight.bold};
		font-size: 128px;
	}

	.LoadingOverlay {
		top: 50%;
		font-weight: ${fontWeight.semiBold};
		font-size: ${fontSize.lg};
	}
`;

const WebCamHeaderStyled = styled.div`
	display: flex;
	justify-content: space-between;

	.timerHeader {
		display: flex;

		${ClickToEditInputStyled} {
			width: 80px;

			> div.disabled {
				border-color: ${colorPalette.gray.c300};
			}

			&.capturedImage {
				width: 320px;
			}

			${InfoIconStyled} {
				right: -25px;
			}
		}
	}

	${FieldStyled} {
		.label {
			margin-left: 16px;
			width: 116px;
		}
	}

	.Button.Capture svg path {
		stroke: ${colorPalette.white};
	}

	.Button svg path {
		stroke: ${colorPalette.primary.c500Primary};
	}

	.Button.Retake:hover {
		svg path {
			stroke: ${colorPalette.primary.c700Dark};
		}
	}
`;

const WebCamStyled = styled.div`
	${SelectInputStyled} {
		width: 280px;
	}

	.buttonContainer {
		display: flex;
		justify-content: flex-end;
		gap: 16px;
		margin-top: 16px;

		${ButtonStyled} {
			width: fit-content;
		}
	}
`;
